import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import CommunityScoreWheel from "@/components/community-score-wheel";
import GameCategories from "@/components/game-categories";
import GameListImage from "@/components/game-list-image";
import {
  DislikeIcon,
  LikeIcon,
  LoveIcon,
} from "@/components/icons/big-rating-icons";
import { gameUrl } from "@/helpers/games";
import { OverallRatingEnum } from "@/pages/search/types";
import type { ReviewGameType, UserType } from "../../types";
import ReviewSummary from "./review-summary";

interface ReviewItemProps {
  game: ReviewGameType;
  isCurrentUser: boolean;
  user: UserType;
}

export default function ReviewItem({
  game,
  isCurrentUser,
  user,
}: ReviewItemProps) {
  const icon = useMemo(() => {
    if (isCurrentUser) {
      return game.communityRatingCount && game.communityScorePercentages ? (
        <div className="flex size-7.5 shrink-0 items-center justify-center rounded-[3px] bg-white/10">
          <CommunityScoreWheel
            className="size-[17px]"
            scores={game.communityScorePercentages}
          />
        </div>
      ) : null;
    }

    if (!game.userReview) {
      return null;
    }

    const { overallRating } = game.userReview;

    if (overallRating === OverallRatingEnum.DISLIKE) {
      return <DislikeIcon />;
    }
    if (overallRating === OverallRatingEnum.LIKE) {
      return <LikeIcon />;
    }
    if (overallRating === OverallRatingEnum.LOVE) {
      return <LoveIcon />;
    }

    return null;
  }, [game, isCurrentUser]);

  return (
    <article className="flex flex-col items-stretch gap-[15px]">
      <div className="relative z-1 flex gap-2.5 md:gap-4">
        <GameListImage
          className="size-15 md:size-17"
          game={game}
          withoutActions
        />
        <div className="flex grow flex-col items-stretch">
          <h2 className="font-medium text-13 text-white leading-[1.2em] md:font-semibold md:text-14">
            <Link className="link-overlay hover:underline" to={gameUrl(game)}>
              {game.name}
            </Link>
          </h2>
          <h3 className="mt-1 font-normal text-12 text-white/80 leading-[1.2em] md:font-medium md:text-pale-silver">
            {game.location.displayName || game.location.company.name}
          </h3>
          <GameCategories
            categories={[game.primaryCategory, ...game.secondaryCategories]}
            className="mt-1.5 gap-1 md:mt-[5px] md:gap-1.5"
          />
        </div>
        {icon}
      </div>
      {!!game.userReview && (
        <ReviewSummary review={game.userReview} user={user} />
      )}
    </article>
  );
}
